import * as React from 'react';
import { isBrowserAuthConnection } from 'vet-bones/utils';

import { useAppDispatch } from 'src/ui/app/hooks';
import {
  Connection,
  connectionInvalid,
  setConnectionBrowserAuthFailed,
  setConnectionDegraded,
  setConnectionSSOError,
  setConnectionUnauthorized,
} from 'src/ui/features/connection';
import { showSignInDialog } from 'src/ui/features/ui';
import { Connection as GraphConnection } from 'src/ui/graph/types';
import {
  getSSOTokenErrorCode,
  isConnectionTimeoutError,
  useVerifyConnection,
} from 'src/ui/hooks/connection';

export const useValidateConnection = () => {
  const dispatch = useAppDispatch();
  const { mutate: validate } = useVerifyConnection();

  return React.useCallback(
    (connection: GraphConnection | null) =>
      new Promise<boolean>((res) => {
        if (!connection || connection?.isAllocating) {
          res(false);
          return;
        }

        const isUsingStardogToken = Boolean(
          !connection?.useSSO && connection?.token
        );

        validate(connection as Connection, {
          onSuccess: () => res(true),
          onError: (error) => {
            if (isConnectionTimeoutError(error)) {
              dispatch(setConnectionUnauthorized(true));
              res(false);
              return;
            }

            const ssoCode = getSSOTokenErrorCode(error);
            if (ssoCode) {
              dispatch(setConnectionSSOError(ssoCode));
              res(false);
              return;
            }

            if (isUsingStardogToken) {
              // If they are using Stardog token it has expired and they need
              // re-enter their password to get a new token.
              dispatch(connectionInvalid(connection.index));
              dispatch(showSignInDialog(true));
            } else if (connection.useSSO) {
              // If they are using sso, then there they are most likely not
              // authorized or some other error with communication
              dispatch(setConnectionUnauthorized(true));
            } else if (isBrowserAuthConnection(connection)) {
              // If useBrowserAuth was enabled and validate connection failed,
              // show banner warning for this
              dispatch(setConnectionBrowserAuthFailed(true));
            } else {
              // If they are not using sso and don't have a token, then Stardog
              // does not support tokens (most likely) show configuration helper
              dispatch(setConnectionDegraded(true));
            }
            res(false);
          },
        });
      }),
    [dispatch, validate]
  );
};
